import {
  type Side,
  Props as TooltipProps,
} from "@/atoms/tooltips/Tooltip/Tooltip";
import {
  SelectOption,
  Props as SelectProps,
} from "@/molecules/selects/Select/Select";
import React, {
  JSX,
  cloneElement,
  forwardRef,
  useEffect,
  useState,
} from "react";
import { useMedia } from "react-use";

import { cn } from "@oneleet/utils";

import * as RadixDropdownMenu from "@radix-ui/react-dropdown-menu";
import { type VariantProps, cva } from "class-variance-authority";
import { AnimatePresence, motion } from "framer-motion";
import { Check } from "lucide-react";

const dropdownMenu = cva(
  "z-[60] mt-2 min-w-[13rem] max-w-xs rounded-md bg-white shadow-lg ring-1 ring-neutral-200 focus:outline-none",
);

type DropdownMenuVariantProps = VariantProps<typeof dropdownMenu>;

export interface DropdownMenuItem {
  type?: "item" | "checkbox" | "separator";
  label?: string;
  sublabel?: string;
  icon?: React.ReactElement<{
    className?: string;
  }>;
  endIcon?: React.ReactElement<{
    className?: string;
  }>;
  link?: React.ReactElement<{
    href: string;
    onClick?: (event: React.MouseEvent) => void;
    className?: string;
  }>;
  tooltip?: React.ReactElement<Pick<TooltipProps, "side">>;
  select?: React.ReactElement<SelectProps<unknown>>;
  isChecked?: boolean;
  isDisabled?: boolean;
  className?: string;
  onSelect?: () => void;
}

type Alignment = "start" | "center" | "end";

export interface Props extends DropdownMenuVariantProps {
  items: DropdownMenuItem[];
  header?: React.ReactNode;
  /**
   * Where to align the dropdown menu relative to the trigger
   */
  align?: Alignment;
  isDisabled?: boolean;
  children: JSX.Element;
  className?: string;
  classNames?: {
    item?: string;
  };
  onOpenChange?: (isOpen: boolean) => void;
}

/**
 * Displays a popup with a list of available actions
 */
const DropdownMenu = forwardRef<
  React.ElementRef<typeof RadixDropdownMenu.Content>,
  Props
>(
  (
    {
      items,
      header,
      align = "end",
      isDisabled,
      children,
      className,
      classNames,
      onOpenChange,
    },
    ref,
  ) => {
    const isMobile = useMedia("(max-width: 640px)");
    const [isOpen, setIsOpen] = useState(false);
    const [hoveredItem, setHoveredItem] = useState<DropdownMenuItem | null>(
      null,
    );

    useEffect(() => {
      if (!isOpen) {
        setHoveredItem(null);
      }
    }, [isOpen]);

    items = items.map((item) => ({ type: "item", ...item }));

    let styledChildren;
    try {
      const childrenClassNames = children.props as { className?: string };
      styledChildren = cloneElement(children, {
        className: cn("focus:outline-none", childrenClassNames.className),
      });
    } catch (_) {
      // children won't have .className in this case, so no need to apply styles
      styledChildren = children;
    }

    let defaultTooltipSide: Side = isMobile ? "bottom" : "left";
    if (!isMobile) {
      switch (align) {
        case "end":
          defaultTooltipSide = "left";
          break;
        case "start":
          defaultTooltipSide = "right";
          break;
        case "center":
          defaultTooltipSide = "left";
          break;
      }
    }

    return (
      <RadixDropdownMenu.Root
        open={isOpen}
        onOpenChange={(_isOpen) => {
          setIsOpen(_isOpen);
          onOpenChange?.(_isOpen);
        }}
      >
        <RadixDropdownMenu.Trigger asChild disabled={isDisabled}>
          {styledChildren}
        </RadixDropdownMenu.Trigger>
        <AnimatePresence>
          {isOpen && (
            <RadixDropdownMenu.Portal forceMount>
              <RadixDropdownMenu.Content asChild align={align} ref={ref}>
                <motion.div
                  initial="enter"
                  animate="visible"
                  exit="leave"
                  variants={{
                    enter: {
                      opacity: 0,
                      scale: 0.95,
                    },
                    visible: {
                      opacity: 1,
                      scale: 1,
                      transition: {
                        ease: "easeOut",
                        duration: 0.1,
                      },
                    },
                    leave: {
                      opacity: 0,
                      scale: 0.95,
                      transition: {
                        ease: "easeIn",
                        duration: 0.075,
                      },
                    },
                  }}
                  className={cn(dropdownMenu(), className)}
                >
                  {header && header}
                  {items.map((item, index) => {
                    return (
                      <DropdownMenuItem
                        item={item}
                        isHovering={hoveredItem?.label === item.label}
                        defaultTooltipSide={defaultTooltipSide}
                        classNames={classNames}
                        key={
                          item.type === "separator"
                            ? `separator-${index}`
                            : item.label
                        }
                        onLinkClick={() => setIsOpen(false)}
                        onMouseEnter={() => setHoveredItem(item)}
                        onSelectSelect={(value) => {
                          // Prevent the select and dropdown menu from closing when clearing the searchbar input (no clue why this triggers)
                          if (value !== null) {
                            setHoveredItem(null);
                            setIsOpen(false);
                          }
                        }}
                      />
                    );
                  })}
                </motion.div>
              </RadixDropdownMenu.Content>
            </RadixDropdownMenu.Portal>
          )}
        </AnimatePresence>
      </RadixDropdownMenu.Root>
    );
  },
);

DropdownMenu.displayName = "DropdownMenu";

interface DropdownMenuItemProps {
  item: DropdownMenuItem;
  isHovering: boolean;
  defaultTooltipSide: Side;
  classNames?: {
    item?: string;
  };
  onLinkClick: () => void;
  onMouseEnter?: () => void;
  onSelectSelect: (
    value: SelectOption<unknown> | SelectOption<unknown>[],
  ) => void;
}

function DropdownMenuItem({
  item,
  isHovering,
  defaultTooltipSide,
  classNames,
  onLinkClick,
  onMouseEnter,
  onSelectSelect,
}: DropdownMenuItemProps) {
  const itemRef = React.useRef<HTMLDivElement | null>(null);

  const styledIcon = item.icon
    ? cloneElement(item.icon, {
        className: cn(
          "text-neutral-550 size-4 shrink-0",
          item.sublabel ? "mt-0.5 self-start" : "",
          item.icon.props.className,
        ),
      })
    : null;

  const styledEndIcon = item.endIcon
    ? cloneElement(item.endIcon, {
        className: cn(
          "text-neutral-450 size-4 shrink-0",
          item.endIcon.props.className,
        ),
      })
    : null;

  if (item.type === "separator") {
    return (
      <RadixDropdownMenu.Separator className="border-neutral-150 border-b" />
    );
  }

  return (
    <RadixDropdownMenu.Item
      ref={itemRef}
      className={cn(
        // Add hover effect in addition to the focus effect for items with select popouts, since the item loses focus when the select is opened
        "first:rounded-t-md last:rounded-b-md hover:bg-neutral-100 focus:bg-neutral-100 focus:outline-none",
        item.isDisabled
          ? "radix-disabled:cursor-default radix-disabled:opacity-25"
          : "cursor-pointer",
        classNames?.item,
        item.className,
      )}
      disabled={item.isDisabled}
      onSelect={(event) => {
        if (item.select) {
          // Prevent the dropdown menu from closing when selecting an item that has a select popout
          event.preventDefault();
          onMouseEnter?.();
        }
        item.onSelect?.();
      }}
      onMouseEnter={() => {
        // Focus items on hover since we've disabled the default onPointerMove focus behavior below
        onMouseEnter?.();
        itemRef.current?.focus();
      }}
      // Prevent dropdown items from stealing focus from the sub-select when the mouse is moved
      // https://github.com/radix-ui/primitives/issues/2193
      onPointerMove={(event) => event.preventDefault()}
    >
      <OptionalLink
        item={item}
        onClick={() => {
          onLinkClick();
          item.onSelect?.();
        }}
      >
        <OptionalTooltip item={item} defaultSide={defaultTooltipSide}>
          <span>
            <OptionalSelect
              item={item}
              isHovering={isHovering}
              onSelect={onSelectSelect}
            >
              <div className="flex items-center justify-between gap-3 px-3 py-2">
                <div className="flex items-center gap-3">
                  {styledIcon}
                  <div className="flex flex-col">
                    <span className="text-ng-md font-ng-medium">
                      {item.label}
                    </span>
                    {item.sublabel && (
                      <span className="text-ng-sm text-neutral-500">
                        {item.sublabel}
                      </span>
                    )}
                  </div>
                </div>
                {item.type === "checkbox" ? (
                  <Check
                    className={cn("h-4 w-4", item.isChecked ? "" : "invisible")}
                  />
                ) : styledEndIcon ? (
                  styledEndIcon
                ) : null}
              </div>
            </OptionalSelect>
          </span>
        </OptionalTooltip>
      </OptionalLink>
    </RadixDropdownMenu.Item>
  );
}

function OptionalSelect({
  item,
  isHovering,
  children,
  onSelect,
}: {
  item: DropdownMenuItem;
  isHovering: boolean;
  children: React.ReactNode;
  onSelect: (value: SelectOption<unknown> | SelectOption<unknown>[]) => void;
}) {
  const selectContainerRef = React.useRef<HTMLDivElement | null>(null);
  if (!item.select) {
    return children;
  }

  return (
    <div ref={selectContainerRef}>
      {cloneElement(item.select, {
        isOpen: isHovering,
        container: selectContainerRef.current ?? undefined,
        onSelect: (value: SelectOption<unknown> | SelectOption<unknown>[]) => {
          item.select?.props.onSelect?.(
            value as SelectOption<unknown> & SelectOption<unknown>[],
          );
          onSelect(value);
        },
        onInputKeyDown: (event) => {
          // Prevent the dropdown menu from interfering with the select's keydown event, which prevents entering spaces
          event.stopPropagation();
        },
        children,
      } as SelectProps<unknown>)}
    </div>
  );
}

function OptionalTooltip({
  item,
  defaultSide,
  children,
}: {
  item: DropdownMenuItem;
  defaultSide: Side;
  children: React.ReactNode;
}) {
  if (!item.tooltip) {
    return children;
  }

  return cloneElement(
    item.tooltip,
    { side: item.tooltip.props.side ?? defaultSide },
    children,
  );
}

function OptionalLink({
  item,
  children,
  onClick,
}: {
  item: DropdownMenuItem;
  children: React.ReactNode;
  onClick: () => void;
}) {
  if (!item.link) {
    return children;
  }

  return cloneElement(
    item.link,
    {
      onClick: (event: React.MouseEvent) => {
        onClick();
        if (item.link?.props.onClick) {
          item.link?.props.onClick(event);
        }
      },
    },
    children,
  );
}

export default DropdownMenu;
